import { getSupabaseBaseUrl } from '@/services/edgeFunctionEndpoints';

export interface RuntimeIssue {
  routePath?: string;
  error: unknown;
  endpoint?: string;
  statusCode?: number | string | null;
}

export function getRuntimeErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  if (typeof error === 'string' && error) return error;
  return 'Unknown runtime error';
}

export function getRuntimeStatusCode(error: unknown): number | string | null {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    code?: unknown;
  };
  const status = candidate.status ?? candidate.statusCode ?? candidate.code ?? null;
  if (typeof status === 'number' || typeof status === 'string') return status;
  return null;
}

export function logRuntimeIssue(label: string, issue: RuntimeIssue): void {
  let apiBase = 'unknown';
  try {
    apiBase = getSupabaseBaseUrl();
  } catch {
    apiBase = 'missing';
  }
  console.warn(`[runtime:${label}]`, {
    routePath: issue.routePath ?? window.location.pathname,
    errorMessage: getRuntimeErrorMessage(issue.error),
    endpoint: issue.endpoint ?? 'unknown',
    statusCode: issue.statusCode ?? getRuntimeStatusCode(issue.error),
    apiBase,
  });
}

export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  endpoint: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(Object.assign(new Error(`Request timeout after ${timeoutMs}ms`), {
        statusCode: 'timeout',
        endpoint,
      }));
    }, timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}